import { apiDownload } from "./client";

/**
 * The two CSV dialects the API writes (`ADR 0003`-adjacent, but a different
 * problem: this one is about what opens the file).
 *
 * "CSV" names two incompatible formats. A German Excel reads a comma as a
 * decimal separator, so a comma-delimited file with German numbers puts every
 * amount in two columns; RFC 4180 says the delimiter *is* a comma. Guessing
 * wrong does not fail loudly — the file opens, and it is wrong.
 */
export type ExportDialect = "excel" | "standard";

export interface ExportOptions {
  dialect?: ExportDialect;
  from?: string;
  to?: string;
  accountId?: string;
}

/** Builds the export URL for a household. */
export function exportPath(householdId: string, options: ExportOptions = {}): string {
  const params = new URLSearchParams();
  if (options.dialect) params.set("dialect", options.dialect);
  if (options.from) params.set("from", options.from);
  if (options.to) params.set("to", options.to);
  if (options.accountId) params.set("account_id", options.accountId);

  const query = params.toString();
  return `/api/v1/households/${householdId}/transactions/export${query ? `?${query}` : ""}`;
}

/**
 * Downloads the ledger and hands it to the browser to save.
 *
 * The object URL is revoked afterwards. Without that, every export in a session
 * pins its whole file in memory until the tab is closed — which for a household
 * with four years of bookings is not a rounding error.
 */
export async function downloadExport(
  householdId: string,
  options: ExportOptions = {},
): Promise<string> {
  const { blob, filename } = await apiDownload(
    exportPath(householdId, options),
    "kontora-buchungen.csv",
  );

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return filename;
}
